'use client';

import React, { FC } from 'react';
import { Link } from '@/navigation';
import useAuth from '@/context/AuthContext';
import {
  TypeGlobalNavFields,
  TypeLink_externalFields,
  TypeLink_internal,
} from '@/core/generated-types';
import { slugBuilder } from '@/core/contentfulConfig';
import { CONTENT_TYPE_LINK } from '@/core/constants';
import { useUserMenuLinks } from '../../hooks/useUserMenuLinks';
import { Translations } from '@/utils/translations/generateTranslationObject';
import { GenericAccordion } from '../../GenericAccordion/GenericAccordion';
import { UserIcon } from '../../UserIcon/UserIcon';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  NAV_EVENT_TYPES,
  PAGE_LOCATIONS,
  trackAsyncItemClickedEvent,
  trackElementPresented,
  trackItemClickedEvent,
} from '@/utils/rudderstack';
import LineAndBlock from '@/components/checkout/Skeletons/LineAndBlock';

export interface UserMenuProps {
  signInLink: TypeGlobalNavFields['signInLink'];
  linkMenuLoggedIn: TypeGlobalNavFields['linkMenuLoggedIn'];
  translations: Translations;
  onClose?: () => void;
}

export const UserMenu: FC<UserMenuProps> = ({
  signInLink,
  linkMenuLoggedIn,
  translations,
  onClose,
}) => {
  const { user } = useAuth();
  const { hardcodedLinks, isLoading } = useUserMenuLinks(translations);

  // Unauthenticated user
  if (!user) {
    const isExternalSignInLink =
      signInLink.sys.contentType.sys.id === CONTENT_TYPE_LINK.EXTERNAL;
    const signInHref = isExternalSignInLink
      ? (signInLink.fields as TypeLink_externalFields).linkUrl || '/'
      : slugBuilder(signInLink as TypeLink_internal);

    return (
      <div className="px-4">
        <Link
          href={signInHref}
          className="w-full py-4 text-left flex items-center text-lg text-charcoal gap-3"
          onClick={async (e) => {
            e.preventDefault();

            await trackAsyncItemClickedEvent({
              url: window.location.href,
              type: CLICKED_ITEM_TYPES.IMAGE,
              pageLocation: PAGE_LOCATIONS.NAVIGATION,
              cta: CTA_NAMES.USER_ICON,
            });

            window.location.href = signInHref;
          }}
        >
          <span>{translations.signIn || 'Sign In'}</span>
        </Link>
      </div>
    );
  }

  // Authenticated user
  return (
    <GenericAccordion
      className="border-b border-transparent last:border-b-0 px-4"
      header={
        <div className="flex items-center gap-2 max-w-[300px]">
          <UserIcon className="flex-shrink-0" />
          <span
            className="whitespace-nowrap truncate"
            data-testid="user-greeting-mobile"
          >
            {translations.hello || 'Hello'}, {user.attributes?.name || 'User'}
          </span>
        </div>
      }
      headerClassName="justify-start gap-4 text-base w-full py-4 flex items-center text-charcoal pr-1.5"
      transitionDuration={300}
      maxHeight="1000px"
      showChevron
      onToggle={(isOpen) => {
        isOpen &&
          trackElementPresented({
            name: NAV_EVENT_TYPES.SECONDARY_NAV_EXPANDED,
            title: 'User Menu',
          });
      }}
    >
      <div className="pl-[40px] pb-4 flex flex-col gap-3">
        {isLoading ? (
          <LineAndBlock numberOfLines={4} lineClassName="!h-4" />
        ) : (
          <>
            {/* Contentful links after hardcoded links */}
            {linkMenuLoggedIn?.map((link) => {
              const isExternalLink =
                link.sys.contentType.sys.id === CONTENT_TYPE_LINK.EXTERNAL;
              const href = isExternalLink
                ? (link.fields as any).linkUrl || '#'
                : slugBuilder(link as any);

              return (
                <Link
                  key={link.sys.id}
                  href={href}
                  className="hover:text-blue-deep text-base py-1"
                  onClick={() => {
                    trackItemClickedEvent({
                      type: CLICKED_ITEM_TYPES.LINK,
                      cta: link.fields.linkText || '',
                      pageLocation: PAGE_LOCATIONS.NAVIGATION,
                      url: window.location.href,
                    });

                    onClose?.();
                  }}
                >
                  {link.fields.linkText}
                </Link>
              );
            })}

            {/* Hardcoded links first */}
            {hardcodedLinks?.map((link) => (
              <a
                key={link.id}
                href={link.href}
                onClick={() => {
                  trackItemClickedEvent({
                    type: CLICKED_ITEM_TYPES.LINK,
                    cta: link.label,
                    pageLocation: PAGE_LOCATIONS.NAVIGATION,
                    url: window.location.href,
                  });

                  link.onClick?.();
                }}
                className="hover:text-blue-deep text-base py-1"
              >
                {link.label}
              </a>
            ))}
          </>
        )}
      </div>
    </GenericAccordion>
  );
};

export default React.memo(UserMenu);
